<script setup>
import { computed, ref } from 'vue'
import Layout from '../../Components/Shared/LayoutAdmin.vue'
import HeaderAdminComponent from '../../Components/Shared/HeaderAdminComponent.vue'
import AdminProductionMap from '../../Components/Map/AdminProductionMap.vue'

const props = defineProps({
    farms: { type: Array, default: () => [] },
    harvests: { type: Array, default: () => [] },
    summary: { type: Object, required: true },
})

const search = ref('')
const normalizedSearch = computed(() => search.value.trim().toLocaleLowerCase())
const matches = row => !normalizedSearch.value || Object.values(row).some(value => String(value ?? '').toLocaleLowerCase().includes(normalizedSearch.value))
const filteredFarms = computed(() => props.farms.filter(matches))
const filteredHarvests = computed(() => props.harvests.filter(matches))
const productGroups = computed(() => {
    const groups = filteredHarvests.value.reduce((result, harvest) => {
        const product = harvest.product || 'Sin producto'

        if (!result[product]) {
            result[product] = {
                name: product,
                harvests: 0,
                activeOffers: 0,
                farms: new Set(),
                producers: new Set(),
            }
        }

        result[product].harvests += 1
        result[product].activeOffers += harvest.offer_status === 'active' ? 1 : 0
        if (harvest.farm) result[product].farms.add(harvest.farm)
        if (harvest.producer) result[product].producers.add(harvest.producer)

        return result
    }, {})

    return Object.values(groups)
        .map(group => ({
            ...group,
            farmCount: group.farms.size,
            producerCount: group.producers.size,
            farmNames: Array.from(group.farms).sort().join(', '),
        }))
        .sort((first, second) => first.name.localeCompare(second.name, 'es'))
})

const mapLink = row => `https://www.google.com/maps?q=${encodeURIComponent(row.latitude)},${encodeURIComponent(row.longitude)}`
const statusLabel = status => ({
    active: 'Publicada',
    scheduled: 'Programada',
    paused: 'Pausada',
    sold_out: 'Agotada',
    expired: 'Vencida',
    draft: 'Borrador',
    not_published: 'No publicada',
}[status] || status)

const formatPrice = row => row.price === null
    ? '—'
    : `${row.currency} ${new Intl.NumberFormat('en-US', { style: 'currency', currency: row.currency, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 4 }).format(Number(row.price))} / ${row.unit}`
</script>

<template>
    <Layout>
        <HeaderAdminComponent title="Producción y fincas" />
        <main class="w-full px-4 py-8 mx-auto md:px-10 max-w-screen-2xl">
            <div class="max-w-3xl">
                <p class="text-sm font-bold tracking-wider uppercase text-light_green">Vista administrativa privada</p>
                <h1 class="mt-2 text-3xl font-bold text-enriqueto_green">Panorama agrícola</h1>
                <p class="mt-3 leading-relaxed text-gray">Consulta las fincas, productores, productos y ubicaciones exactas registradas. Esta información no se expone en las páginas públicas de ofertas.</p>
            </div>

            <section class="grid grid-cols-2 gap-4 mt-7 lg:grid-cols-4">
                <article class="p-5 border rounded-2xl border-light_gray bg-light_yellow"><p class="text-sm text-gray">Fincas</p><strong class="block mt-1 text-3xl text-enriqueto_green">{{ summary.farms }}</strong></article>
                <article class="p-5 border rounded-2xl border-light_gray bg-light_yellow"><p class="text-sm text-gray">Cosechas</p><strong class="block mt-1 text-3xl text-enriqueto_green">{{ summary.harvests }}</strong></article>
                <article class="p-5 border rounded-2xl border-light_gray bg-light_yellow"><p class="text-sm text-gray">Ofertas activas</p><strong class="block mt-1 text-3xl text-enriqueto_green">{{ summary.active_offers }}</strong></article>
                <article class="p-5 border rounded-2xl border-light_gray bg-light_yellow"><p class="text-sm text-gray">Productores</p><strong class="block mt-1 text-3xl text-enriqueto_green">{{ summary.producers }}</strong></article>
            </section>

            <label class="block max-w-xl mt-7">
                <span class="text-sm font-bold text-enriqueto_green">Buscar productor, finca, producto o municipio</span>
                <input v-model="search" type="search" class="w-full p-3 mt-2 bg-white border rounded-xl border-light_gray" placeholder="Escribe para filtrar las tablas">
            </label>

            <section class="mt-9">
                <header class="flex flex-col gap-2 mb-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p class="text-sm font-bold tracking-wider uppercase text-light_green">Ubicación privada administrativa</p>
                        <h2 class="mt-1 text-2xl font-bold text-enriqueto_green">Mapa global de fincas</h2>
                    </div>
                    <p class="text-sm text-gray">{{ filteredFarms.length }} {{ filteredFarms.length === 1 ? 'finca visible' : 'fincas visibles' }}</p>
                </header>

                <AdminProductionMap :farms="filteredFarms" />
            </section>

            <section class="mt-9 overflow-hidden bg-white border shadow-sm rounded-3xl border-light_gray">
                <header class="p-5 border-b md:p-6 bg-light_yellow border-light_gray">
                    <h2 class="text-xl font-bold text-enriqueto_green">Productos registrados</h2>
                    <p class="mt-1 text-sm text-gray">Resumen de los productos presentes en las fincas mostradas en el mapa.</p>
                </header>

                <div v-if="productGroups.length" class="grid grid-cols-1 gap-px bg-light_gray sm:grid-cols-2 xl:grid-cols-3">
                    <article v-for="product in productGroups" :key="product.name" class="p-5 bg-white md:p-6">
                        <div class="flex items-start justify-between gap-4">
                            <h3 class="text-lg font-bold text-enriqueto_green">{{ product.name }}</h3>
                            <span v-if="product.activeOffers" class="px-3 py-1 text-xs font-bold rounded-full bg-light_yellow text-enriqueto_green">
                                {{ product.activeOffers }} {{ product.activeOffers === 1 ? 'oferta' : 'ofertas' }}
                            </span>
                        </div>
                        <p class="mt-3 text-sm text-gray">
                            {{ product.harvests }} {{ product.harvests === 1 ? 'cosecha' : 'cosechas' }} ·
                            {{ product.farmCount }} {{ product.farmCount === 1 ? 'finca' : 'fincas' }} ·
                            {{ product.producerCount }} {{ product.producerCount === 1 ? 'productor' : 'productores' }}
                        </p>
                        <p class="mt-2 text-sm text-enriqueto_green">{{ product.farmNames }}</p>
                    </article>
                </div>
                <p v-else class="p-8 text-center text-gray">No hay productos que coincidan con la búsqueda.</p>
            </section>

            <section class="mt-9 overflow-hidden bg-white border shadow-sm rounded-3xl border-light_gray">
                <header class="p-5 border-b md:p-6 bg-light_yellow border-light_gray">
                    <h2 class="text-xl font-bold text-enriqueto_green">Fincas y ubicación exacta</h2>
                    <p class="mt-1 text-sm text-gray">{{ filteredFarms.length }} registros</p>
                </header>
                <div class="overflow-x-auto">
                    <table class="w-full text-sm text-left">
                        <thead class="text-enriqueto_green"><tr><th class="px-5 py-4">Finca</th><th class="px-5 py-4">Productor</th><th class="px-5 py-4">Tamaño</th><th class="px-5 py-4">Coordenadas</th><th class="px-5 py-4">Cosechas</th><th class="px-5 py-4">Mapa</th></tr></thead>
                        <tbody>
                            <tr v-for="farm in filteredFarms" :key="farm.id" class="border-t border-light_gray hover:bg-light_yellow">
                                <td class="px-5 py-4 font-bold text-enriqueto_green">{{ farm.name }}</td>
                                <td class="px-5 py-4">{{ farm.producer }}<br><span class="text-xs text-gray">{{ farm.email }}</span></td>
                                <td class="px-5 py-4 whitespace-nowrap">{{ farm.size }} {{ farm.area_unit === 'ha' ? 'ha' : 'm²' }}</td>
                                <td class="px-5 py-4 font-mono text-xs whitespace-nowrap">{{ farm.latitude }}, {{ farm.longitude }}</td>
                                <td class="px-5 py-4 text-center">{{ farm.harvest_count }}</td>
                                <td class="px-5 py-4"><a :href="mapLink(farm)" target="_blank" rel="noopener" class="font-bold underline text-enriqueto_green">Abrir mapa</a></td>
                            </tr>
                            <tr v-if="filteredFarms.length === 0"><td colspan="6" class="p-8 text-center text-gray">No hay fincas que coincidan con la búsqueda.</td></tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section class="mt-9 overflow-hidden bg-white border shadow-sm rounded-3xl border-light_gray">
                <header class="p-5 border-b md:p-6 bg-light_yellow border-light_gray">
                    <h2 class="text-xl font-bold text-enriqueto_green">Cosechas y publicación</h2>
                    <p class="mt-1 text-sm text-gray">{{ filteredHarvests.length }} registros</p>
                </header>
                <div class="overflow-x-auto">
                    <table class="w-full text-sm text-left">
                        <thead class="text-enriqueto_green"><tr><th class="px-5 py-4">Producto</th><th class="px-5 py-4">Cosecha</th><th class="px-5 py-4">Finca / productor</th><th class="px-5 py-4">Estado público</th><th class="px-5 py-4">Precio</th><th class="px-5 py-4">Ubicación pública</th><th class="px-5 py-4">Ubicación exacta</th></tr></thead>
                        <tbody>
                            <tr v-for="harvest in filteredHarvests" :key="harvest.id" class="border-t border-light_gray hover:bg-light_yellow">
                                <td class="px-5 py-4 font-bold text-enriqueto_green">{{ harvest.product }}</td>
                                <td class="px-5 py-4">{{ harvest.name }}</td>
                                <td class="px-5 py-4">{{ harvest.farm }}<br><span class="text-xs text-gray">{{ harvest.producer }}</span></td>
                                <td class="px-5 py-4 whitespace-nowrap">{{ statusLabel(harvest.offer_status) }}</td>
                                <td class="px-5 py-4 whitespace-nowrap">{{ formatPrice(harvest) }}</td>
                                <td class="px-5 py-4">{{ [harvest.municipality, harvest.department].filter(Boolean).join(', ') || '—' }}</td>
                                <td class="px-5 py-4"><a :href="mapLink(harvest)" target="_blank" rel="noopener" class="font-bold underline text-enriqueto_green">Abrir mapa</a></td>
                            </tr>
                            <tr v-if="filteredHarvests.length === 0"><td colspan="7" class="p-8 text-center text-gray">No hay cosechas que coincidan con la búsqueda.</td></tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </main>
    </Layout>
</template>
